import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useContext, useState, type ComponentPropsWithRef, type ReactElement, type ReactNode } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverIconClose,
    PopoverTitle,
    PopoverTrigger,
    type PopoverContentProps,
    type PopoverTriggerProps,
} from '../containers/popover.tsx';
import { PushStack, PushStackLevel } from '../containers/push-stack.tsx';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { checkboxVariants } from '../primitives/checkbox.tsx';
import { Menu, MenuItem, MenuItemLabel, MenuItemLeading, MenuItemTrailing } from '../primitives/menu.tsx';
import { cn } from '../util/classes.ts';
import { useControllableState } from '../util/use-controllable-state.tsx';
import {
    FilterMenuContext,
    type FilterMenuCategory,
    type FilterMenuContextValue,
    type FilterMenuOption,
    type FilterMenuSelection,
} from './filter-menu-context.ts';

export type { FilterMenuCategory, FilterMenuOption, FilterMenuSelection };

function useFilterMenu(): FilterMenuContextValue {
    const context = useContext(FilterMenuContext);

    if (!context) {
        throw new Error('FilterMenu parts must be rendered inside <FilterMenu>');
    }

    return context;
}

/** Drops categories whose selection is empty, so `{}` always means "no filters". */
function prune(selection: FilterMenuSelection): FilterMenuSelection {
    return Object.fromEntries(Object.entries(selection).filter(([, values]) => values.length > 0));
}

function sameValues(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((value) => b.includes(value));
}

export type FilterMenuProps = {
    categories: FilterMenuCategory[];
    /** The applied selection. Re-seeds the draft every time the menu opens. */
    selection?: FilterMenuSelection;
    /** `FilterMenuApply` hands over the whole draft and closes the menu. */
    onApply: (selection: FilterMenuSelection) => void;
    /** `FilterMenuClear` emptied the draft. Nothing is applied until Apply. */
    onClearAll?: () => void;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Which category level 2 shows; `undefined` is level 1. An applied chip
     * re-opens the menu on its own category through this. */
    categoryKey?: string;
    onCategoryKeyChange?: (categoryKey: string | undefined) => void;
    children?: ReactNode;
};

/**
 * Two-level filter menu: categories, then one category's options, inside a
 * single surface — a popover on desktop, a bottom sheet below `sm` (both come
 * from `Popover`). Selections are drafted internally, so edits across several
 * categories commit together on `FilterMenuApply` instead of per click.
 *
 * @example
 * <FilterMenu categories={categories} selection={applied} onApply={setApplied}>
 *     <FilterMenuTrigger render={<AddFilterButton>Filter</AddFilterButton>} />
 *     <FilterMenuContent title="Filter" closeLabel="Close" backLabel="Back">
 *         <FilterMenuFooter>
 *             <FilterMenuClear>Clear all</FilterMenuClear>
 *             <FilterMenuApply>Apply</FilterMenuApply>
 *         </FilterMenuFooter>
 *     </FilterMenuContent>
 * </FilterMenu>
 */
export function FilterMenu({
    categories,
    selection = {},
    onApply,
    onClearAll,
    open,
    defaultOpen = false,
    onOpenChange,
    categoryKey,
    onCategoryKeyChange,
    children,
}: FilterMenuProps): ReactElement {
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });

    // `undefined` means level 1 here, so the usual "undefined = uncontrolled"
    // test cannot be used; the presence of a change handler decides instead.
    const [internalCategoryKey, setInternalCategoryKey] = useState(categoryKey);
    const activeCategoryKey = onCategoryKeyChange ? categoryKey : internalCategoryKey;
    const openCategory = onCategoryKeyChange ?? setInternalCategoryKey;

    // Kept behind the active key so the level-2 panel still has its category
    // while it slides back out.
    const [exitingCategoryKey, setExitingCategoryKey] = useState(activeCategoryKey);
    if (activeCategoryKey !== undefined && activeCategoryKey !== exitingCategoryKey) {
        setExitingCategoryKey(activeCategoryKey);
    }

    const [draft, setDraft] = useState(selection);
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (wasOpen !== isOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            setDraft(selection);
        }
    }

    const touchedKeys = new Set([...Object.keys(selection), ...Object.keys(draft)]);
    const isDirty = [...touchedKeys].some((key) => !sameValues(draft[key] ?? [], selection[key] ?? []));
    const hasSelection = Object.values(draft).some((values) => values.length > 0);

    const close = (): void => {
        setOpen(false);
        if (!onCategoryKeyChange) {
            setInternalCategoryKey(undefined);
        }
    };

    const context: FilterMenuContextValue = {
        categories,
        draft,
        isDirty,
        hasSelection,
        activeCategoryKey,
        exitingCategoryKey,
        openCategory,
        toggleValue: (key, value) => {
            const single = categories.find((entry) => entry.key === key)?.single ?? false;

            setDraft((previous) => {
                const values = previous[key] ?? [];

                if (values.includes(value)) {
                    return { ...previous, [key]: values.filter((current) => current !== value) };
                }

                return { ...previous, [key]: single ? [value] : [...values, value] };
            });
        },
        clearDraft: () => {
            setDraft({});
            onClearAll?.();
        },
        applyDraft: () => {
            onApply(prune(draft));
            close();
        },
    };

    return (
        <FilterMenuContext.Provider value={context}>
            <Popover
                open={isOpen}
                onOpenChange={(next) => {
                    if (next) {
                        setOpen(true);
                        return;
                    }

                    close();
                }}
            >
                {children}
            </Popover>
        </FilterMenuContext.Provider>
    );
}

export type FilterMenuTriggerProps = PopoverTriggerProps;

/** The control that opens the menu — pass your own via `render`. */
export function FilterMenuTrigger({ render, ...props }: FilterMenuTriggerProps): ReactElement {
    return <PopoverTrigger data-slot="filter-menu-trigger" render={render} {...props} />;
}

export type FilterMenuContentProps = Omit<PopoverContentProps, 'title'> & {
    /** Sheet title on the mobile level-1 header, and the surface's accessible name. */
    title: string;
    /** Accessible name for the mobile close ×. */
    closeLabel: string;
    /** Prefix read before the category name on the level-2 back row, e.g. "Back". */
    backLabel: string;
};

/** The surface: both levels, the push transition between them, and your footer. */
export function FilterMenuContent({
    title,
    closeLabel,
    backLabel,
    // The surface takes focus itself: landing on the first category would ring
    // a row nobody picked, and the levels swap what that row even is.
    initialFocus = false,
    className,
    children,
    ...props
}: FilterMenuContentProps): ReactElement {
    const { categories, activeCategoryKey, exitingCategoryKey } = useFilterMenu();

    const atDetail = activeCategoryKey !== undefined;
    const category = categories.find((entry) => entry.key === (activeCategoryKey ?? exitingCategoryKey));

    return (
        <PopoverContent
            data-slot="filter-menu-content"
            // Above `sm` the card has no visible title, so it is named here.
            aria-label={title}
            initialFocus={initialFocus}
            className={cn('sm:w-72', className)}
            {...props}
        >
            {/* The visible headers slide with their level, so the surface's
                accessible name lives outside the stack where it stays put. */}
            <PopoverTitle className="sr-only">{atDetail ? (category?.label ?? title) : title}</PopoverTitle>
            <div data-slot="filter-menu" className="flex min-h-0 flex-col">
                <PushStack activeIndex={atDetail ? 1 : 0}>
                    <PushStackLevel data-slot="filter-menu-level-1" className="flex w-full flex-col">
                        <FilterMenuCategoryList title={title} closeLabel={closeLabel} />
                    </PushStackLevel>
                    <PushStackLevel data-slot="filter-menu-level-2" className="flex w-full flex-col">
                        {category && (
                            <FilterMenuOptionList
                                category={category}
                                backLabel={backLabel}
                                closeLabel={closeLabel}
                            />
                        )}
                    </PushStackLevel>
                </PushStack>
                {children}
            </div>
        </PopoverContent>
    );
}

type FilterMenuCategoryListProps = {
    title: string;
    closeLabel: string;
};

/** Level 1 — the categories, each with the count it would contribute. */
function FilterMenuCategoryList({ title, closeLabel }: FilterMenuCategoryListProps): ReactElement {
    const { categories, draft, openCategory } = useFilterMenu();

    return (
        <>
            <div className="flex items-center justify-between gap-default px-default pt-default sm:hidden">
                <span className="min-w-0 flex-1 truncate text-card-title">{title}</span>
                <PopoverIconClose label={closeLabel} />
            </div>
            <Menu>
                {categories.map((entry) => {
                    const count = (draft[entry.key] ?? []).length;

                    return (
                        <MenuItem
                            key={entry.key}
                            label={entry.label}
                            data-slot="filter-menu-category"
                            onClick={() => {
                                openCategory(entry.key);
                            }}
                        >
                            <MenuItemLabel />
                            <MenuItemTrailing>
                                {count > 0 && (
                                    <span
                                        data-slot="filter-menu-count"
                                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand/(--opacity-wash) px-tight text-ui-sm font-medium text-brand tabular-nums"
                                    >
                                        {count}
                                    </span>
                                )}
                                <ChevronRightIcon />
                            </MenuItemTrailing>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

type FilterMenuOptionListProps = {
    category: FilterMenuCategory;
    backLabel: string;
    closeLabel: string;
};

/** Level 2 — a category's options, under a back row the scrolling list can't carry. */
function FilterMenuOptionList({ category, backLabel, closeLabel }: FilterMenuOptionListProps): ReactElement {
    const { draft, openCategory, toggleValue } = useFilterMenu();

    return (
        <>
            <div className="flex items-center gap-tight pr-cozy">
                {/* A row, not bespoke chrome — `MenuItem` already owns the
                    height, hover and press of one. */}
                <Menu className="min-w-0 flex-1 pb-0">
                    <MenuItem
                        label={`${backLabel} ${category.label}`}
                        data-slot="filter-menu-back"
                        onClick={() => {
                            openCategory(undefined);
                        }}
                    >
                        <MenuItemLeading>
                            <ChevronLeftIcon />
                        </MenuItemLeading>
                        <MenuItemLabel>
                            <span className="truncate text-card-title">{category.label}</span>
                        </MenuItemLabel>
                    </MenuItem>
                </Menu>
                <PopoverIconClose label={closeLabel} />
            </div>
            <Menu className="max-h-80 overflow-auto overscroll-contain">
                {category.options.map((option) => {
                    const checked = (draft[category.key] ?? []).includes(option.value);

                    return (
                        <MenuItem
                            key={option.value}
                            label={option.label}
                            role={category.single ? 'menuitemradio' : 'menuitemcheckbox'}
                            aria-checked={checked}
                            data-slot="filter-menu-option"
                            onClick={() => {
                                toggleValue(category.key, option.value);
                            }}
                        >
                            {/* A checkbox would promise the other options still fit. */}
                            {!category.single && (
                                <MenuItemLeading>
                                    <span
                                        aria-hidden
                                        data-state={checked ? 'checked' : 'unchecked'}
                                        className={checkboxVariants({ variant: 'primary' })}
                                    />
                                </MenuItemLeading>
                            )}
                            <MenuItemLabel />
                            {option.count !== undefined && (
                                <MenuItemTrailing className="text-caption tabular-nums">
                                    {option.count}
                                </MenuItemTrailing>
                            )}
                            {category.single && checked && (
                                <MenuItemTrailing>
                                    <CheckIcon className="stroke-[2.5] text-brand" />
                                </MenuItemTrailing>
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

export type FilterMenuFooterProps = ComponentPropsWithRef<'div'>;

/** Spans both levels and stays put while they slide. */
export function FilterMenuFooter({ className, ...props }: FilterMenuFooterProps): ReactElement {
    return (
        <div
            data-slot="filter-menu-footer"
            className={cn(
                'flex shrink-0 items-center justify-end gap-cozy border-t border-line-weak px-default py-cozy',
                className,
            )}
            {...props}
        />
    );
}

export type FilterMenuClearProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

/** Empties the draft. The menu stays open — Apply still has to commit it. */
export function FilterMenuClear({ variant = 'secondary', ...props }: FilterMenuClearProps): ReactElement {
    const { clearDraft, hasSelection } = useFilterMenu();

    return (
        <Button
            data-slot="filter-menu-clear"
            variant={variant}
            {...props}
            disabled={!hasSelection}
            onClick={clearDraft}
        />
    );
}

export type FilterMenuApplyProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

/** Commits the draft and closes. Disabled while the draft matches what is applied. */
export function FilterMenuApply({ variant = 'primary', ...props }: FilterMenuApplyProps): ReactElement {
    const { applyDraft, isDirty } = useFilterMenu();

    return (
        <Button
            data-slot="filter-menu-apply"
            variant={variant}
            {...props}
            disabled={!isDirty}
            onClick={applyDraft}
        />
    );
}
